import {
  LOGIN_REQUEST,
  LOGIN_SUCCESS,
  LOGIN_FAILURE,
  LOGOUT_SUCCESS,
  REFRESH_TOKEN_REQUEST,
  REFRESH_TOKEN_SUCCESS,
  REFRESH_TOKEN_FAILURE
} from '../../actions/userActions'

/**
 * Borra sólo lo que compone la sesión.
 *
 * `localStorage.clear()` arrasaba con todo el almacenamiento del dominio, no
 * sólo con las claves de autenticación. Aquí se enumeran, que además documenta
 * cuáles son.
 */
const limpiarSesion = () => {
  ;[
    'accessToken',
    'token',
    'refreshToken',
    'adminId',
    'permissions',
    'groups',
    'cities',
    'rememberMe'
  ].forEach(clave => localStorage.removeItem(clave))
}

export default function reducer(
  state = {
    isAuthenticated: !!localStorage.getItem('accessToken'),
    isFetching: false,
    isRefreshing: false,
    data: {},
    errors: {},
    groups: JSON.parse(localStorage.getItem('groups') || 'null'),
    permissions: JSON.parse(localStorage.getItem('permissions') || 'null'),
    cities: JSON.parse(localStorage.getItem('cities') || 'null')
  },
  action
) {
  switch (action.type) {
    case LOGIN_REQUEST:
      return {
        ...state,
        isFetching: true,
        isAuthenticated: false,
        errors: {},
        creds: action.creds
      }

    case LOGIN_SUCCESS:
      // Guardar tokens
      localStorage.setItem('accessToken', action.data.access)
      localStorage.setItem('refreshToken', action.data.refresh)
      // Mantener 'token' para compatibilidad con exportaciones de Excel
      localStorage.setItem('token', action.data.access)

      // Guardar datos del admin
      localStorage.setItem('adminId', action.data.admin.id)
      localStorage.setItem(
        'permissions',
        JSON.stringify(action.data.admin.permissions)
      )
      localStorage.setItem('groups', JSON.stringify(action.data.admin.groups))
      localStorage.setItem('cities', JSON.stringify(action.data.admin.cities))

      return {
        ...state,
        isFetching: false,
        isAuthenticated: true,
        data: action.data.admin,
        groups: action.data.admin.groups,
        permissions: action.data.admin.permissions,
        cities: action.data.admin.cities,
        errors: {}
      }

    case LOGIN_FAILURE:
      console.error('❌ Reducer: LOGIN_FAILURE', action.errors)
      return {
        ...state,
        isFetching: false,
        isAuthenticated: false,
        errors: action.errors
      }

    case REFRESH_TOKEN_REQUEST:
      console.log('🔄 Reducer: REFRESH_TOKEN_REQUEST')
      return {
        ...state,
        isRefreshing: true,
        // Se anota con qué refresh token se sale, para distinguir luego «me lo
        // rechazaron porque la sesión terminó» de «me lo rechazaron porque otra
        // pestaña ya lo rotó». Ver REFRESH_TOKEN_FAILURE.
        tokenEnUso: localStorage.getItem('refreshToken')
      }

    case REFRESH_TOKEN_SUCCESS:
      console.log('✅ Reducer: REFRESH_TOKEN_SUCCESS')

      // Actualizar access tokens (ambas llaves)
      localStorage.setItem('accessToken', action.data.access)
      localStorage.setItem('token', action.data.access)

      // Si viene un nuevo refresh token, actualizarlo también
      if (action.data.refresh) {
        localStorage.setItem('refreshToken', action.data.refresh)
      }

      return {
        ...state,
        isRefreshing: false,
        isAuthenticated: true,
        errors: {}
      }

    case REFRESH_TOKEN_FAILURE: {
      /**
       * Sólo un rechazo explícito cierra la sesión.
       *
       * Antes, **cualquier** fallo del refresco hacía `localStorage.clear()`: un
       * corte de red, un 502 del gateway, un timeout o un CORS expulsaban al
       * operador igual que un token caducado de verdad. Eso explicaba por qué
       * «a unos usuarios sí y a otros no» — dependía de la calidad de su
       * conexión y de si pillaban un hipo del backend a mitad de faena.
       *
       * El código HTTP viaja en la acción desde URBWEB-4349:
       *
       * - **401 / 403** — el servidor rechazó el refresh token. Ya no hay nada
       *   que hacer: se cierra la sesión.
       * - **Cualquier otra cosa, o ninguna** — no pudimos hablar con el
       *   servidor, o el servidor tuvo un problema suyo. El refresh token
       *   probablemente siga siendo válido, así que **se conserva la sesión** y
       *   el siguiente intento —el temporizador de `MainContainer` reintenta
       *   cada minuto— lo resolverá.
       */
      const rechazado = action.status === 401 || action.status === 403

      if (!rechazado) {
        console.warn(
          '⚠️ El refresco de sesión falló sin que el servidor rechazara el ' +
            'token. Se conserva la sesión y se reintentará.',
          action.errors
        )
        return {
          ...state,
          isRefreshing: false,
          errors: action.errors
        }
      }

      /**
       * Antes de darla por perdida: ¿lo rotó otra pestaña?
       *
       * La operación trabaja con varias pestañas abiertas. Todas comparten
       * `localStorage`, así que cuando dos temporizadores coinciden ambas salen
       * con el **mismo** refresh token. El backend rota al atender la primera, y
       * la segunda llega con uno ya consumido: un 401 legítimo, con la sesión
       * perfectamente viva.
       *
       * Si el token que hay ahora en `localStorage` no es el que usamos, otra
       * pestaña renovó mientras tanto y no hay nada que cerrar.
       */
      const guardado = localStorage.getItem('refreshToken')
      if (guardado && state.tokenEnUso && guardado !== state.tokenEnUso) {
        console.warn(
          '⚠️ El refresh token fue rechazado, pero otra pestaña ya lo había ' +
            'renovado. Se conserva la sesión.'
        )
        return { ...state, isRefreshing: false }
      }

      console.error(
        '❌ Refresh token rechazado por el servidor. Cerrando sesión.'
      )
      limpiarSesion()

      return {
        ...state,
        isRefreshing: false,
        isAuthenticated: false,
        data: {},
        groups: null,
        permissions: null,
        cities: null,
        errors: action.errors
      }
    }

    case LOGOUT_SUCCESS:
      localStorage.clear()
      return {
        ...state,
        isFetching: false,
        isAuthenticated: false,
        data: {},
        groups: null,
        permissions: null,
        cities: null,
        errors: {}
      }

    default:
      return state
  }
}
